/* eslint-disable no-console */
import React, {Fragment, memo, useEffect, useRef, useState} from 'react';
import {
  ResponsiveContainer,
  BarChart as BarRe,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Rectangle
} from 'recharts';

import {formatNumber} from '../../../pages/netZero/utils/commonFunctions';
import {sortBy} from '../../../shared/commonFunctions';
import {splitSentenceIntoTwo} from '../../../utils/commonFunctions';

import BarChartTooltip from './utils/BarChartTooltip';
import {divideIntoSixParts} from './utils/commonFunctions';

// import {convertPxToRem} from '../../../utils/commonFunctions';

const CustomizedXAxisTick: React.FC<{
  x: number;
  y: number;
  payload: any;
  rotate?: boolean;
}> = ({x, y, payload, rotate = false}) => {
  const label = rotate ? payload?.value : splitSentenceIntoTwo(payload?.value);

  return (
    <g transform={`translate(${x},${y})`} key={payload?.value}>
      {rotate ? (
        <text
          x={0}
          y={0}
          dy={rotate ? 5 : 16}
          textAnchor="start"
          transform="rotate(90)"
          style={{font: 'normal normal 400 12px/15px Montserrat', fill: '#666'}}
        >
          {payload?.value}
        </text>
      ) : (
        <text
          x={0}
          y={0}
          dy={16}
          fill="#666"
          style={{font: 'normal normal 400 12px/15px Montserrat'}}
        >
          {label?.map((item: any, index: number) =>
            item ? (
              <tspan
                textAnchor="middle"
                x="0"
                dy={index === 0 ? '15' : '16'}
                key={index}
              >
                {item}
              </tspan>
            ) : (
              <Fragment key={index}></Fragment>
            )
          )}
        </text>
      )}
    </g>
  );
};

const BarChart: React.FC<{
  data: any[];
  isColorReverse?: boolean;
  mainData?: any;
}> = ({data, mainData, isColorReverse = false}) => {
  if (
    data?.every((item: any) => {
      return item?.value === 0 || typeof item?.value !== 'number';
    })
  ) {
    data = [];
  }
  const [tooltipPosition, setTooltipPosition] = useState({
    x: 0,
    y: 0,
    barWidth: 0,
    show: false
  });
  const tooltipOffset = 30;
  const [width, setWidth] = useState(0);
  const tooltipRef = useRef(null);
  const rotateAfter = 3;
  // Calculate dynamic height for rotated labels
  const calculateXAxisHeight = () => {
    if (data?.length > rotateAfter) {
      // When labels are rotated, height should be based on longest label
      const maxLabelLength = Math.max(
        ...data.map((item) => item?.source?.length || 0)
      );
      // Approximate: 7px per character (accounting for font size and spacing)
      const calculatedHeight = Math.max(maxLabelLength * 7, 80);
      // Cap at reasonable maximum to prevent extremely tall charts

      return Math.min(calculatedHeight, 300);
    }

    return 40; // Default height for non-rotated labels
  };

  const xAxisHeight = calculateXAxisHeight();

  useEffect(() => {
    if (tooltipRef.current) {
      const {offsetWidth} = tooltipRef.current;
      const totalWidth = tooltipPosition.barWidth - offsetWidth;
      setWidth(totalWidth / 2);
    } else {
      setWidth(0);
    }
  }, [tooltipPosition]);

  const checkMax = Math.ceil(Math.max(...data.map((item) => item.value)) * 1.2);

  const max = Number.isFinite(checkMax) ? checkMax : 100;
  const divideIntoSix = divideIntoSixParts(max);
  const divideIntoSixFormat = divideIntoSixParts(max).map((item) => {
    return `${formatNumber(item)} ${data?.[0]?.unit ?? ''}`.length;
  });
  const showChart = divideIntoSix.every((item) => {
    return item >= 0;
  });
  const color = mainData?.fill ?? '#1A79FF';

  const colorHex = isColorReverse
    ? {tooltipBg: '#5697F8', barColor: '#F76D7B'}
    : {tooltipBg: '#FF3366', barColor: color};

  return showChart ? (
    <ResponsiveContainer width="100%" height="100%">
      <BarRe data={sortBy(data, 'value', 'dec')}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="source"
          axisLine={false}
          tickLine={false}
          tick={(props) => (
            <CustomizedXAxisTick
              {...props}
              rotate={data?.length > rotateAfter}
            />
          )}
          height={xAxisHeight}
          includeHidden
          interval={0}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          domain={showChart ? [0, max] : undefined}
          // unit={` ${data?.[0]?.unit ?? ''}`}
          style={{font: 'normal normal 400 12px/15px Montserrat'}}
          ticks={showChart ? divideIntoSix : undefined}
          tickFormatter={(value) => {
            return `${formatNumber(value)} ${data?.[0]?.unit ?? ''}`;
          }}
          width={Math.max(...divideIntoSixFormat) * 8}
        />
        <Tooltip
          cursor={false}
          position={{
            x: tooltipPosition.x + width,
            y: tooltipPosition.y
          }}
          content={({payload}: any) => {
            return (
              <BarChartTooltip
                active={tooltipPosition.show}
                payload={payload}
                tooltipRef={tooltipRef}
                bgColor={colorHex.tooltipBg}
              />
            );
          }}
          isAnimationActive={false}
        />
        <Bar
          style={{width: '100%', height: '100%'}}
          dataKey="value"
          shape={<Rectangle fill={colorHex.barColor} radius={10} />}
          barSize={data?.length <= 3 ? 50 : 20}
          minPointSize={24}
          onMouseEnter={(e) => {
            setTooltipPosition({
              x: e.x,
              y: e.y - tooltipOffset,
              barWidth: e.width,
              show: true
            });
          }}
          onMouseOut={(e) => {
            setTooltipPosition({
              x: e.x,
              y: e.y - tooltipOffset,
              barWidth: e.width,
              show: false
            });
          }}
        />
      </BarRe>
    </ResponsiveContainer>
  ) : (
    <></>
  );
};

export default memo(BarChart);
